/**
 * Simple i18n translation system for KrownFT frontend.
 *
 * All UI text is in Spanish. This module provides a centralized
 * location for all translatable strings used across React components.
 *
 * Usage:
 *   import { t } from "@/lib/i18n"
 *   <span>{t.navigation.home}</span>
 *   <span>{t.common.save}</span>
 */

export const t = {
  navigation: {
    home: "Inicio",
    search: "Buscar",
    opportunities: "Oportunidades",
    tryouts: "Pruebas",
    messages: "Mensajes",
    profile: "Perfil",
    settings: "Configuración",
    signOut: "Cerrar sesión",
    signIn: "Iniciar sesión",
    signUp: "Registrarse",
    notifications: "Notificaciones",
  },

  common: {
    save: "Guardar",
    saveChanges: "Guardar cambios",
    cancel: "Cancelar",
    edit: "Editar",
    delete: "Eliminar",
    confirm: "Confirmar",
    back: "Volver",
    next: "Siguiente",
    previous: "Anterior",
    submit: "Enviar",
    loading: "Cargando...",
    search: "Buscar",
    filter: "Filtrar",
    clearFilters: "Limpiar filtros",
    noResults: "No se encontraron resultados",
    showMore: "Ver más",
    showAll: "Ver todos",
    showLess: "Ver menos",
    publish: "Publicar",
    add: "Agregar",
    remove: "Quitar",
    close: "Cerrar",
  },

  auth: {
    signIn: "Iniciar sesión",
    signUp: "Registrarse",
    signOut: "Cerrar sesión",
    email: "Email",
    password: "Contraseña",
    passwordConfirmation: "Confirmar contraseña",
    forgotPassword: "¿Olvidaste tu contraseña?",
  },

  roles: {
    player: "Jugador",
    scout: "Scout",
    club: "Club",
    admin: "Administrador",
  },

  onboarding: {
    roleSelection: {
      title: "¿Qué tipo de cuenta querés crear?",
      player: {
        title: "Soy Jugador",
        description: "Creá tu perfil y mostrá tu talento",
      },
      scout: {
        title: "Soy Scout / Representante",
        description: "Descubrí jugadores con potencial",
      },
      club: {
        title: "Soy Club",
        description: "Encontrá el talento que tu equipo necesita",
      },
    },
    playerSetup: {
      title: "Completá tu perfil",
      steps: {
        basicInfo: "Datos básicos",
        sportsInfo: "Info deportiva",
        currentSituation: "Situación actual",
        aboutMe: "Sobre mí",
        videoCv: "Video CV",
      },
    },
  },

  player: {
    positions: {
      portero: "Portero",
      defensa_central: "Defensa Central",
      lateral_derecho: "Lateral Derecho",
      lateral_izquierdo: "Lateral Izquierdo",
      mediocampista_central: "Mediocampista Central",
      mediocampista_ofensivo: "Mediocampista Ofensivo",
      mediocampista_defensivo: "Mediocampista Defensivo",
      extremo_derecho: "Extremo Derecho",
      extremo_izquierdo: "Extremo Izquierdo",
      delantero_centro: "Delantero Centro",
      segundo_delantero: "Segundo Delantero",
    } as Record<string, string>,
    dominantFoot: {
      derecho: "Derecho",
      izquierdo: "Izquierdo",
      ambidiestro: "Ambidiestro",
    } as Record<string, string>,
    availability: {
      disponible: "Disponible",
      no_disponible: "No disponible",
      escuchando_ofertas: "Escuchando ofertas",
    } as Record<string, string>,
    visibility: {
      publico: "Público",
      solo_scouts: "Solo scouts",
      privado: "Privado",
    } as Record<string, string>,
    fields: {
      position: "Posición",
      secondaryPosition: "Posición secundaria",
      dominantFoot: "Pie dominante",
      dateOfBirth: "Fecha de nacimiento",
      nationality: "Nacionalidad",
      country: "País",
      region: "Provincia/Estado",
      city: "Ciudad",
      heightCm: "Altura (cm)",
      weightKg: "Peso (kg)",
      bio: "Biografía",
      idol: "Ídolo",
      currentClub: "Club actual",
      currentDivision: "División actual",
      availability: "Disponibilidad",
      visibility: "Visibilidad",
    },
    sections: {
      personalData: "Datos Personales",
      sportsInfo: "Info Deportiva",
      currentSituation: "Situación Actual",
      aboutMe: "Sobre Mí",
      stats: "Estadísticas",
      highlights: "Highlights",
    },
    profile: {
      followers: "seguidores",
      follower: "seguidor",
      follow: "Seguir",
      following: "Siguiendo",
      sendMessage: "Enviar mensaje",
      yearsOld: "años",
      noStats: "Aún no se agregaron estadísticas",
      noHighlights: "Aún no se agregaron highlights",
      noViews: "Aún no hay visitas a tu perfil",
    },
    seasons: {
      addSeason: "Agregar temporada",
      noSeasons: "Aún no agregaste temporadas",
      seasonName: "Nombre de temporada",
      year: "Año",
      clubName: "Club",
      division: "División",
      matchesPlayed: "PJ",
      goals: "Goles",
      assists: "Asist.",
      yellowCards: "TA",
      redCards: "TR",
      minutesPlayed: "Min.",
    },
    highlights: {
      addHighlight: "Agregar highlight",
      noHighlights: "Aún no agregaste highlights",
      pinned: "Fijado",
      views: "vistas",
    },
  },

  search: {
    placeholder: "Buscar jugadores por nombre, ciudad, país...",
    filters: "Filtros",
    position: "Posición",
    country: "País",
    dominantFoot: "Pie dominante",
    ageRange: "Rango de edad",
    minAge: "Edad mín.",
    maxAge: "Edad máx.",
    resultsCount: (count: number) =>
      count === 1 ? "1 jugador encontrado" : `${count} jugadores encontrados`,
    noResults: "No se encontraron jugadores con los filtros seleccionados.",
    clearFilters: "Limpiar filtros",
  },

  feed: {
    tabs: {
      following: "Siguiendo",
      explore: "Explorar",
    },
    composer: {
      placeholder: "¿Qué querés compartir?",
      publish: "Publicar",
      visibility: {
        publico: "Público",
        seguidores: "Seguidores",
        privado: "Privado",
      },
    },
    post: {
      like: "Me gusta",
      comment: "Comentar",
      share: "Compartir",
      deleteConfirm: "¿Estás seguro de que querés eliminar esta publicación?",
    },
    noPosts: "No hay publicaciones",
    noPostsFollowing: "Seguí a otros usuarios para ver sus publicaciones acá.",
    noPostsExplore: "Aún no hay publicaciones para explorar.",
  },

  conversations: {
    title: "Mensajes",
    noConversations: "No tenés conversaciones",
    noConversationsMessage:
      "Iniciá una conversación enviando un mensaje a un jugador o scout.",
    startConversation: "Iniciá la conversación",
    sendMessage: "Enviar mensaje",
    typePlaceholder: "Escribí un mensaje...",
    markAllRead: "Marcar todas como leídas",
  },

  notifications: {
    title: "Notificaciones",
    noNotifications: "No tenés notificaciones",
    markAllRead: "Marcar todas como leídas",
    viewAll: "Ver todas",
    actions: {
      followed: "te empezó a seguir",
      liked_post: "le gustó tu publicación",
      post_commented: "comentó tu publicación",
      opportunity_applied: "se postuló a tu oportunidad",
      application_status_changed: "actualizó el estado de tu postulación",
      mentioned: "te mencionó en una publicación",
      tryout_registration: "se inscribió a tu prueba",
    } as Record<string, string>,
  },

  opportunities: {
    title: "Oportunidades",
    create: "Crear oportunidad",
    apply: "Postularme",
    alreadyApplied: "Ya te postulaste",
    noOpportunities: "No hay oportunidades disponibles",
    filters: {
      position: "Posición",
      country: "País",
    },
    sort: {
      newest: "Más recientes",
      expiringSoon: "Próximas a vencer",
    },
    status: {
      abierta: "Abierta",
      cerrada: "Cerrada",
      pausada: "Pausada",
    },
    applicationStatus: {
      pendiente: "Pendiente",
      vista: "Vista",
      contactado: "Contactado",
      rechazada: "Rechazada",
    },
    form: {
      title: "Título",
      description: "Descripción",
      positionNeeded: "Posición requerida",
      ageRange: "Rango de edad",
      minAge: "Edad mínima",
      maxAge: "Edad máxima",
      country: "País",
      region: "Provincia/Estado",
      division: "División",
      expiresAt: "Fecha de vencimiento",
    },
  },

  savedPlayers: {
    title: "Jugadores guardados",
    noSavedPlayers: "Aún no guardaste ningún jugador",
    noSavedPlayersMessage:
      "Guardá jugadores desde la búsqueda para verlos acá.",
    remove: "Quitar",
    notes: "Notas",
    savedOn: "Guardado el",
  },

  moderation: {
    report: "Reportar",
    reportContent: "Reportar contenido",
    block: "Bloquear",
    unblock: "Desbloquear",
    reportReasons: {
      spam: "Spam",
      acoso: "Acoso",
      contenido_inapropiado: "Contenido inapropiado",
      perfil_falso: "Perfil falso",
      otro: "Otro",
    },
    reportDescription: "Descripción (opcional)",
    reportSubmit: "Enviar reporte",
  },

  landing: {
    hero: {
      tagline: "Donde el talento se encuentra con la oportunidad",
      subtitle:
        "La plataforma que conecta jugadores de fútbol con scouts y clubes de todo el mundo.",
    },
    cta: {
      player: "Soy Jugador",
      seekTalent: "Busco Talento",
    },
    featuredPlayers: "Jugadores Destacados",
    stats: {
      players: "Jugadores",
      opportunities: "Oportunidades",
      clubs: "Clubes",
    },
  },

  time: {
    now: "ahora",
    minutesAgo: (n: number) => `hace ${n} min`,
    hoursAgo: (n: number) => `hace ${n} h`,
    yesterday: "ayer",
    daysAgo: (n: number) => `hace ${n} días`,
  },

  pagination: {
    previous: "Anterior",
    next: "Siguiente",
    pageOf: (current: number, total: number) => `Página ${current} de ${total}`,
  },
} as const
